import { useEffect, useRef, useState, useCallback } from 'react';
import BpmnJS from 'bpmn-js';
import type { BpmnViewerInstance, ExecutionHistory } from '../types/bpmn.types';
import { bpmnApi } from '../api/bpmnApi';

interface UseBpmnViewerOptions {
  processDefinitionKey: string;
  processInstanceId?: string;
  showExecutionState?: boolean;
}

interface UseBpmnViewerResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  error: string | null;
  viewer: BpmnViewerInstance | null;
  executionHistory: ExecutionHistory | null;
  zoomIn: () => void;
  zoomOut: () => void;
  fitViewport: () => void;
  exportSvg: () => Promise<string | null>;
  exportPng: () => Promise<Blob | null>;
}

const OVERLAY_STYLES = {
  active: 'background: #1976d2; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold;',
  completed: 'background: #2e7d32; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px;',
  incident: 'background: #d32f2f; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px;',
};

export function useBpmnViewer({
  processDefinitionKey,
  processInstanceId,
  showExecutionState = true,
}: UseBpmnViewerOptions): UseBpmnViewerResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<BpmnViewerInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executionHistory, setExecutionHistory] = useState<ExecutionHistory | null>(null);

  // Initialize viewer
  useEffect(() => {
    if (!containerRef.current) return;

    const viewer = new BpmnJS({
      container: containerRef.current,
    });

    viewerRef.current = viewer;

    const loadDiagram = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const xml = await bpmnApi.getProcessDefinitionXml(processDefinitionKey);
        await viewer.importXML(xml);

        const canvas = viewer.get('canvas') as { zoom: (level: string | number) => void };
        canvas.zoom('fit-viewport');
      } catch (err) {
        console.error('Failed to load BPMN diagram:', err);
        setError(err instanceof Error ? err.message : 'Fehler beim Laden des BPMN-Diagramms');
      } finally {
        setIsLoading(false);
      }
    };

    loadDiagram();

    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [processDefinitionKey]);

  // Load execution history and apply overlays
  useEffect(() => {
    if (!processInstanceId || !showExecutionState || !viewerRef.current) return;

    const loadExecutionState = async () => {
      try {
        const history = await bpmnApi.getExecutionHistory(processInstanceId);
        setExecutionHistory(history);
        applyOverlays(history);
      } catch (err) {
        console.error('Failed to load execution history:', err);
      }
    };

    loadExecutionState();
  }, [processInstanceId, showExecutionState, isLoading]);

  const applyOverlays = useCallback((history: ExecutionHistory) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const overlays = viewer.get('overlays') as {
      add: (elementId: string, type: string, config: { position: { top: number; left: number }; html: string }) => void;
      remove: (filter: { type: string }) => void;
    };
    const elementRegistry = viewer.get('elementRegistry') as {
      get: (id: string) => { id: string; businessObject: { name?: string } } | undefined;
    };

    // Remove existing overlays
    overlays.remove({ type: 'execution-state' });

    // Group activities by activityId
    const activityCounts = new Map<string, { completed: number; active: number }>();

    history.completedActivities.forEach((activity) => {
      const current = activityCounts.get(activity.activityId) || { completed: 0, active: 0 };
      current.completed++;
      activityCounts.set(activity.activityId, current);
    });

    history.activeActivities.forEach((activity) => {
      const current = activityCounts.get(activity.activityId) || { completed: 0, active: 0 };
      current.active++;
      activityCounts.set(activity.activityId, current);
    });

    // Apply overlays for each activity
    activityCounts.forEach((counts, activityId) => {
      const element = elementRegistry.get(activityId);
      if (!element) return;

      if (counts.active > 0) {
        // Active activities - blue badge
        overlays.add(activityId, 'execution-state', {
          position: { top: -10, left: -10 },
          html: `<div style="${OVERLAY_STYLES.active}" title="Aktiv: ${counts.active}">${counts.active}</div>`,
        });
      } else if (counts.completed > 0) {
        // Completed activities - green checkmark
        overlays.add(activityId, 'execution-state', {
          position: { top: -10, left: -10 },
          html: `<div style="${OVERLAY_STYLES.completed}" title="Abgeschlossen: ${counts.completed}">&#10003;</div>`,
        });
      }
    });
  }, []);

  const zoomIn = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const canvas = viewer.get('canvas') as { zoom: (level: string | number, center?: string) => number };
    const currentZoom = canvas.zoom('fit-viewport');
    canvas.zoom(currentZoom * 1.2);
  }, []);

  const zoomOut = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const canvas = viewer.get('canvas') as { zoom: (level: string | number, center?: string) => number };
    const currentZoom = canvas.zoom('fit-viewport');
    canvas.zoom(currentZoom * 0.8);
  }, []);

  const fitViewport = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const canvas = viewer.get('canvas') as { zoom: (level: string) => void };
    canvas.zoom('fit-viewport');
  }, []);

  const exportSvg = useCallback(async (): Promise<string | null> => {
    const viewer = viewerRef.current;
    if (!viewer) return null;
    try {
      const result = await viewer.saveSVG();
      return result.svg;
    } catch (err) {
      console.error('Failed to export SVG:', err);
      return null;
    }
  }, []);

  const exportPng = useCallback(async (): Promise<Blob | null> => {
    const svg = await exportSvg();
    if (!svg) return null;

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      };

      img.onerror = () => resolve(null);
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
    });
  }, [exportSvg]);

  return {
    containerRef,
    isLoading,
    error,
    viewer: viewerRef.current,
    executionHistory,
    zoomIn,
    zoomOut,
    fitViewport,
    exportSvg,
    exportPng,
  };
}
